import {
  IconListDetails,
  IconPlus,
  IconCheck,
  IconTrash,
  IconEdit,
} from "@tabler/icons-react";
import "./TaxProTasks.scss";
import { ActionIcon, Button, Loader, ScrollArea, Tooltip } from "@mantine/core";
import { useState, useRef, useEffect } from "react";
import { useTasks } from "../../../../utils/hooks/useTaxProTasks";
import type { Task } from "../../../../types/tasks";
import { notifications } from "@mantine/notifications";

const MAX_TASK_NAME_LENGTH = 255;

const TaxProTasks = () => {
  const {
    tasks,
    isLoadingTasks,
    isErrorTasks,
    tasksError,
    addTask,
    modifyTask,
    removeTask,
  } = useTasks();

  const [newTaskName, setNewTaskName] = useState("");
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editedTaskName, setEditedTaskName] = useState("");
  const [inputWidth, setInputWidth] = useState<number>(0);
  const [showInput, setShowInput] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Adjust input width based on task name
  useEffect(() => {
    if (inputRef.current) {
      setInputWidth(inputRef.current.scrollWidth);
    }
  }, [editedTaskName]);

  // Handle errors
  if (isErrorTasks) {
    throw tasksError;
  }

  // Add a new task
  const handleAddTask = () => {
    if (newTaskName.trim().length > MAX_TASK_NAME_LENGTH) {
      notifications.show({
        title: "Task Name Too Long",
        message: `Task name must be less than ${MAX_TASK_NAME_LENGTH} characters`,
        color: "red",
      });
      return;
    }
    if (newTaskName.trim()) {
      addTask({ name: newTaskName, completed: false });
      setNewTaskName("");
      setShowInput(false);
    }
  };

  // Toggle task completion
  const toggleTaskCompletion = (task: Task) => {
    modifyTask(task.id, {
      id: task.id,
      name: task.name,
      completed: !task.completed,
    });
  };

  // Delete task
  const handleDeleteTask = (taskId: number) => {
    removeTask(taskId);
  };

  // Handle task editing
  const handleEditTaskClick = (task: Task) => {
    setEditingTaskId(task.id);
    setEditedTaskName(task.name);
  };

  // Submit task name change
  const handleEditTaskSubmit = () => {
    if (editedTaskName.trim() && editingTaskId !== null) {
      modifyTask(editingTaskId, {
        id: editingTaskId,
        name: editedTaskName,
        completed: false,
      });
      setEditingTaskId(null);
      setEditedTaskName("");
    }
  };

  return (
    <div className="tax-pro-tasks-container">
      <div className="tax-pro-tasks">
        <div className="panel-header">
          <div className="panel-title">
            <IconListDetails /> Tasks To Complete
          </div>
          <div className="panel-button">
            <Button
              variant="transparent"
              leftSection={<IconPlus />}
              onClick={() => setShowInput(true)}
            >
              New Task
            </Button>
          </div>
        </div>

        <div className="tasks-to-complete">
          {isLoadingTasks && <Loader />}

          {!isLoadingTasks &&
            !isErrorTasks &&
            (!tasks || tasks.length === 0) &&
            !showInput && (
              <div className="no-tasks">
                <p>No tasks to complete.</p>
              </div>
            )}

          {/* Input for adding new task */}
          {showInput && (
            <div className="add-task">
              <input
                type="text"
                className="task-input"
                placeholder="Enter new task name"
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddTask();
                  }
                }}
              />
              <Tooltip
                label={!newTaskName.trim() ? "Please enter a task" : "Add task"}
              >
                <ActionIcon
                  variant="transparent"
                  color="blue"
                  onClick={handleAddTask}
                  disabled={!newTaskName.trim()}
                  size={30}
                >
                  <IconCheck />
                </ActionIcon>
              </Tooltip>
            </div>
          )}

          {/* Task list */}
          <ScrollArea.Autosize
            type="always"
            mah={200}
            p="md"
            offsetScrollbars
            scrollbars="y"
          >
            <ul>
              {tasks?.map((task) => (
                <li onClick={() => handleEditTaskClick(task)} key={task.id}>
                  <button
                    className={`circular-button ${
                      task.completed ? "checked" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTaskCompletion(task);
                    }}
                  >
                    {task.completed && <IconCheck size={20} />}
                  </button>

                  {editingTaskId === task.id ? (
                    <textarea
                      ref={inputRef}
                      value={editedTaskName}
                      onChange={(e) => setEditedTaskName(e.currentTarget.value)}
                      onBlur={handleEditTaskSubmit}
                      style={{ width: `${inputWidth}px` }}
                      className="task-name edit-input"
                      autoFocus
                    />
                  ) : (
                    <span className="task-name" role="button" tabIndex={0}>
                      {task.name}
                    </span>
                  )}

                  <button
                    className="edit-button"
                    onClick={() => handleEditTaskClick(task)}
                  >
                    <IconEdit size={20} />
                  </button>

                  <button
                    className="delete-button"
                    onClick={() => handleDeleteTask(task.id)}
                  >
                    <IconTrash size={20} />
                  </button>
                </li>
              ))}
            </ul>
          </ScrollArea.Autosize>
        </div>
      </div>
    </div>
  );
};

export default TaxProTasks;
